import {saveAs} from 'file-saver';
import {t} from 'i18next';
import sortBy from 'lodash/sortBy';

import {showNewNotification} from '@edna/components';
import {TCurrency, and, any, buildQueryString} from '@edna/utils';

import {EChannelType, TItemId, XLSX_MIME_TYPE} from 'src/constants';
import {EPermission} from 'src/containers/User';
import {ECacheTag, listenerMiddleware, rootApi} from 'src/models';
import {mapEmptyFieldsToUndefined, parseISO} from 'src/utils';

import {TActiveTariff, TTariffChangeRequest, TTariffHistory, TTariffTemplate} from './definitions';

const api = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getTariffs: builder.query<
      TActiveTariff[],
      {channelType?: EChannelType; subjectId?: TItemId; tenantId?: TItemId}
    >({
      query: ({tenantId, ...params}) => ({
        method: 'GET',
        url: tenantId ? `/billing/tariff/manager/${tenantId}` : '/billing/tariff',
        params,
        meta: {
          isShowError: true,
          permissions: tenantId ? and<EPermission>('PERMISSION_TENANT_TARIFF_READ') : any(),
        },
      }),
      transformResponse: (response: TActiveTariff[]) => sortBy(response, 'channelType'),
      providesTags: [ECacheTag.TARIFFS],
    }),
    acceptTariffs: builder.mutation<void, TTariffTemplate['id'][]>({
      query: (tariffIds) => ({
        method: 'POST',
        // intentional, params don't support array queries
        url: `/billing/tariff/template/accept${buildQueryString({tariffIds})}`,
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.TARIFF_TEMPLATES],
    }),
    createPersonalTariff: builder.mutation<
      TTariffTemplate,
      {
        data: {
          file?: File | null;
          tariffTemplateId?: TItemId;
          licensePrice?: number;
          licenseCurrency?: TCurrency;
          name?: string;

          // whatsapp
          mauCount?: number;
          initPayment?: number;
          defaultMauPrice?: number;
        };
        channelType: EChannelType;
      }
    >({
      query: ({data: {file, ...data}, channelType}) => {
        if (file) {
          const formData = new FormData();

          formData.append('file', file);
          formData.append(
            'request',
            new Blob([JSON.stringify(mapEmptyFieldsToUndefined(data))], {
              type: 'application/json',
            }),
          );

          return {
            method: 'POST',
            url: `/billing/tariff/template/${channelType.toLowerCase()}/create/from-file`,
            data: formData,
            meta: {
              isShowError: true,
              errorMessageKey: 'Tariffs:createPersonalTariff.errorFileUpload',
              isShowSuccess: true,
              successMessageKey: 'Tariffs:createPersonalTariff.successFileUpload',
              i18nOptions: {name: file.name},
            },
          };
        }

        return {
          method: 'POST',
          url: `/billing/tariff/template/${channelType.toLowerCase()}/create`,
          data: mapEmptyFieldsToUndefined(data),
          meta: {
            isShowError: true,
            errorMessageKey: 'Tariffs:createPersonalTariff.errorUpload',
            isShowSuccess: true,
            successMessageKey: 'Tariffs:createPersonalTariff.successUpload',
            i18nOptions: {name: data.name ?? ''},
          },
        };
      },
      invalidatesTags: [ECacheTag.TARIFF_TEMPLATES],
    }),
    updateTariff: builder.mutation<
      TActiveTariff[],
      {
        id: TItemId;
        name: string;
        channelType: EChannelType;
        licensePrice: number;
        file: File;
        activeFrom: string;

        // whatsapp
        mauCount?: number;
        initPayment?: number;
        defaultMauPrice?: number;
        licensePeriodMonths?: number;
      }
    >({
      query: ({
        file,
        id,
        channelType,
        name,
        licensePrice,
        defaultMauPrice,
        mauCount,
        initPayment,
        activeFrom,
        licensePeriodMonths,
      }) => {
        const data = mapEmptyFieldsToUndefined({
          idToUpdate: [id],
          channelType,
          licensePrice,
          newName: name,
          from: activeFrom,
          defaultMauPrice,
          mauCount,
          initPayment,
          licensePeriodMonths,
        });

        if (file) {
          const formData = new FormData();

          formData.append(
            'request',
            new Blob([JSON.stringify({...data, action: 'REPLACE'})], {
              type: 'application/json',
            }),
          );

          formData.append('file', file);

          return {
            method: 'POST',
            url: '/billing/tariff/manager/update',
            data: formData,
            meta: {
              isShowError: true,
              successMessageKey: 'Tariffs:tariffUpdate.scheduled',
              isShowSuccess: true,
              i18nOptions: {date: parseISO(activeFrom, 'dd.MM.yyyy')},
            },
          };
        }

        return {
          method: 'POST',
          url: '/billing/tariff/manager/update-license',
          data,
          meta: {
            isShowError: true,
            successMessageKey: 'Tariffs:tariffUpdate.scheduled',
            isShowSuccess: true,
            i18nOptions: {date: parseISO(activeFrom, 'dd.MM.yyyy')},
          },
        };
      },
      invalidatesTags: [ECacheTag.TARIFFS, ECacheTag.PLANNED_TARIFFS],
    }),
    downloadTariff: builder.mutation<Blob, TActiveTariff | TTariffTemplate>({
      query: ({id}) => ({
        method: 'GET',
        url: `/billing/tariff/manager/${id}/download`,
        responseType: 'blob',
        meta: {isShowError: true, errorMessageKey: 'Common:errorDownload'},
      }),
    }),
    getTariffTemplates: builder.query<
      TTariffTemplate[],
      {
        channelType?: EChannelType;
        tenantId?: TItemId;
        leadId?: TItemId;
        currency?: TCurrency | null;
        partnerUuid?: string;
      } | void
    >({
      query: ({tenantId, ...params} = {}) => {
        if (tenantId) {
          return {
            method: 'GET',
            url: `/billing/tariff/template/manager/${tenantId}`,
            meta: {
              isShowError: true,
              permissions: and<EPermission>('PERMISSION_TARIFF_TEMPLATE_READ'),
            },
          };
        }

        if (params.leadId !== undefined || params.currency || params.partnerUuid) {
          return {
            method: 'POST',
            url: '/billing/tariff/template/manager',
            data: params,
            meta: {
              isShowError: true,
            },
          };
        }

        return {
          method: 'GET',
          url: '/billing/tariff/template',
          params,
          meta: {
            isShowError: true,
          },
        };
      },
      providesTags: [ECacheTag.TARIFF_TEMPLATES],
      transformResponse: (response: TTariffTemplate[]) => sortBy(response, 'channelType'),
    }),
    getUnacceptedTariffTemplates: builder.query<
      TTariffTemplate[],
      {channelType: EChannelType} | void
    >({
      query: (params) => ({
        method: 'GET',
        url: '/billing/tariff/template/unaccepted',
        params,
        meta: {isShowError: true},
      }),
      providesTags: [ECacheTag.TARIFF_TEMPLATES],
    }),
    getTariffHistory: builder.query<
      TTariffHistory[],
      {subjectId: number; channelType: EChannelType; tenantId?: TItemId}
    >({
      query: ({channelType, subjectId, tenantId}) => ({
        method: 'GET',
        url: tenantId
          ? `/billing/tariff/manager/history`
          : `/billing/tariff/${channelType}/subject/${subjectId}`,
        params: tenantId ? {channelType, subjectId, tenantId} : undefined,
        meta: {isShowError: true},
      }),
      transformResponse: (response: TActiveTariff[]) =>
        response
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((tariff: any) => ({...tariff, ...tariff?.payload}) as TTariffHistory)
          .filter(
            (tariff: TTariffHistory) =>
              new Date(tariff.activeFrom).getTime() <= new Date().getTime(),
          )
          .sort(
            (a: TTariffHistory, b: TTariffHistory) =>
              new Date(b.activeFrom).getTime() - new Date(a.activeFrom).getTime(),
          ),
    }),
    getPlannedTariffs: builder.query<TActiveTariff[], TItemId>({
      query: (tenantId) => ({
        method: 'GET',
        url: `/billing/tariff/manager/planned/${tenantId}`,
        meta: {isShowError: true, permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ')},
      }),
      providesTags: [ECacheTag.PLANNED_TARIFFS],
    }),
    getIsTemplateExists: builder.query<{exists: boolean}, {name: string}>({
      query: ({name}) => ({
        method: 'GET',
        url: '/billing/tariff/template/exists',
        params: {name},
        meta: {isShowError: true},
      }),
    }),
    getIsTariffExists: builder.query<{exists: boolean}, {name: string; tenantId?: TItemId}>({
      query: ({name, tenantId}) => ({
        method: 'GET',
        url: '/billing/tariff/manager/exists',
        params: {name, tenantId},
        meta: {isShowError: true},
      }),
    }),
    getNeedToAgreeTariffChangeRequests: builder.query<TTariffChangeRequest[], void>({
      query: () => ({
        method: 'GET',
        url: '/tenantmanagement/change-tariff-request/status/need-to-agree',
        meta: {isShowError: true},
      }),
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.TARIFF_CHANGE_REQUESTS];
        }

        return [
          ECacheTag.TARIFF_CHANGE_REQUESTS,
          ...result.map(({id}) => ({id, type: ECacheTag.TARIFF_CHANGE_REQUESTS})),
        ];
      },
    }),
    agreeTariffChangeRequest: builder.mutation<void, TTariffChangeRequest['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/tenantmanagement/change-tariff-request/${id}/agree`,
        meta: {isShowError: true},
      }),
      invalidatesTags: (_, __, id) => [{type: ECacheTag.TARIFF_CHANGE_REQUESTS, id}],
    }),
    disagreeTariffChangeRequest: builder.mutation<void, TTariffChangeRequest['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/tenantmanagement/change-tariff-request/${id}/not-agree`,
        meta: {isShowError: true},
      }),
      invalidatesTags: (_, __, id) => [{type: ECacheTag.TARIFF_CHANGE_REQUESTS, id}],
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: api.endpoints.downloadTariff.matchFulfilled,
  effect: (action) => {
    const tariff = action.meta.arg.originalArgs;

    saveAs(
      new File(
        [action.payload],
        t('Tariff:activeTariffFileName', {
          tenantId: tariff.tenantId,
          tariffName: `${tariff.channelType.toUpperCase()}${tariff.id}`,
          date: parseISO(new Date(), 'dd.MM.yyyy'),
        }),
        {type: XLSX_MIME_TYPE},
      ),
    );

    showNewNotification({
      type: 'SUCCESS',
      messageKey: 'Common:successDownload',
    });
  },
});

export const {
  useGetTariffsQuery,
  useAcceptTariffsMutation,
  useGetTariffTemplatesQuery,
  useCreatePersonalTariffMutation,
  useGetUnacceptedTariffTemplatesQuery,
  useGetTariffHistoryQuery,
  useAgreeTariffChangeRequestMutation,
  useDisagreeTariffChangeRequestMutation,
  useGetNeedToAgreeTariffChangeRequestsQuery,
  useLazyGetIsTariffExistsQuery,
  useLazyGetIsTemplateExistsQuery,
  useGetPlannedTariffsQuery,
  useUpdateTariffMutation,
  useDownloadTariffMutation,
  endpoints: tariffsEndpoints,
} = api;
